import React from "react";
import { Box, Stack, Skeleton, useTheme, Grid, Paper, Divider } from "@mui/material";
import { keyframes } from "@emotion/react";

// Shimmer animation
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// Glass-style shimmer card - Horizontal layout
const StoryCardShimmer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        borderRadius: 0,
        overflow: "hidden",
        backgroundColor: "transparent",
        backdropFilter: "none",
        border: "none",
        boxShadow: "none",
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "100%",
          height: "100%",
          background: isDark
            ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)"
            : "linear-gradient(90deg, transparent, rgba(0,0,0,0.05), transparent)",
          animation: `${shimmer} 2s infinite`,
        },
      }}
    >
      {/* Badge skeleton - Top Right Corner */}
      <Skeleton
        variant="rectangular"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 3,
          width: 80,
          height: 24,
          borderRadius: 999,
          bgcolor: isDark
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.08)",
        }}
      />

      {/* Image area - Top on Mobile, Left on Desktop */}
      <Skeleton
        variant="rectangular"
        sx={{
          width: { xs: "100%", sm: 180 },
          minWidth: { xs: "100%", sm: 180 },
          aspectRatio: "3/4",
          height: { xs: "auto", sm: 240 }, // 180 * 4/3 = 240 for desktop, auto for mobile
          borderRadius: 0,
          flexShrink: 0,
          bgcolor: isDark
            ? "rgba(255,255,255,0.05)"
            : "rgba(0,0,0,0.05)",
        }}
      />
      
      {/* Content area - Below Image on Mobile, Right Side on Desktop */}
      <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2 }, flexGrow: 1, minWidth: 0 }}>
        {/* Title skeleton */}
        <Skeleton
          variant="text"
          width="85%"
          height={28}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        <Skeleton
          variant="text"
          width="60%"
          height={28}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        {/* Author skeleton */}
        <Skeleton
          variant="text"
          width="40%"
          height={18}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.06)",
          }}
        />
        {/* Description skeleton */}
        <Skeleton
          variant="text"
          width="95%"
          height={16}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        <Skeleton
          variant="text"
          width="90%"
          height={16}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        <Skeleton
          variant="text"
          width="75%"
          height={16}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        {/* Stats skeleton */}
        <Stack direction="row" spacing={2} sx={{ mt: "auto" }}>
          <Skeleton
            variant="rectangular"
            width={50}
            height={20}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
          <Skeleton
            variant="rectangular"
            width={50}
            height={20}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
          <Skeleton
            variant="rectangular"
            width={50}
            height={20}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
        </Stack>
        {/* Tags skeleton */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
          <Skeleton
            variant="rectangular"
            width={80}
            height={24}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
          <Skeleton
            variant="rectangular"
            width={70}
            height={24}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
          <Skeleton
            variant="rectangular"
            width={60}
            height={24}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

// ExplorePage Shimmer - Only shows the grid/content area
const ExplorePageShimmer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const pageCount = 6;

  return (
    <>
      {/* Stats Section Shimmer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2.5, sm: 0 },
          mb: { xs: 3, md: 4 },
          pb: { xs: 3, md: 4 },
          borderBottom: (theme) =>
            `1px solid ${
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
            }`,
        }}
      >
        <Skeleton variant="text" width={150} height={32} />
        <Skeleton variant="rectangular" width={150} height={36} sx={{ borderRadius: 1 }} />
      </Box>

      {/* Grid Shimmer */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: "transparent",
          backdropFilter: "blur(12px)",
          border: (theme) =>
            `1px solid ${
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
            }`,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 2px 8px rgba(0,0,0,0.1)"
              : "0 2px 8px rgba(0,0,0,0.05)",
          p: { xs: 3, md: 4 },
        }}
      >
        <Grid container spacing={0} sx={{ display: "flex", flexWrap: "wrap" }}>
          {Array.from({ length: pageCount }).map((_, index) => {
            const isFirstInRow = index % 2 === 0;
            const isLastInRow = (index + 1) % 2 === 0;
            const isInLastRow = index >= pageCount - (pageCount % 2 === 0 ? 2 : 1);

            return (
              <React.Fragment key={index}>
                <Grid
                  item
                  xs={12}
                  sx={(theme) => ({
                    width: "100%",
                    flex: "0 0 100%",
                    maxWidth: "100%",
                    [theme.breakpoints.up(1100)]: {
                      width: "50%",
                      flex: "0 0 50%",
                      maxWidth: "50%",
                    },
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      p: { xs: 2, sm: 2.5 },
                      height: "100%",
                      width: "100%",
                      maxWidth: "100%",
                      borderRight:
                        isFirstInRow && index < pageCount - 1
                          ? `1px solid ${
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.08)"
                            }`
                          : "none",
                      borderBottom: !isInLastRow
                        ? `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.08)"
                          }`
                        : "none",
                      [theme.breakpoints.up(1100)]: {
                        "&:nth-of-type(2n)": {
                          borderRight: "none",
                        },
                      },
                    })}
                  >
                    <StoryCardShimmer />
                  </Box>
                </Grid>
                {/* Mobile divider between rows */}
                {isLastInRow && !isInLastRow && (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: { xs: "block", sm: "block", md: "none" },
                      [theme.breakpoints.up(1100)]: {
                        display: "none",
                      },
                    }}
                  >
                    <Divider
                      sx={{
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.08)",
                      }}
                    />
                  </Grid>
                )}
              </React.Fragment>
            );
          })}
        </Grid>
      </Paper>

      {/* Pagination Shimmer */}
      <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 6 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            width={40}
            height={40}
            sx={{
              borderRadius: 2,
              bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
            }}
          />
        ))}
      </Stack>
    </>
  );
};

export default ExplorePageShimmer;
